import { useEffect } from 'react';

import { LoginLayout } from '../../components/LoginLayout/LoginLayout';
import { LOGIN_SUB_PASSWORD_ROUTE, LOGIN_SUB_SSO_ROUTE } from '../../constants/routes.constants';
import { useAuthenticationState } from '../../hooks/useAuthenticationState';

import { MFAForm } from './MFASection/MFAForm.container';
import { PasswordLoginSection } from './PasswordLoginSection/PasswordLogin.section';
import { SignupButton } from './SignupButton/SignupButton';
import { SsoLoginSection } from './SsoLoginSection/SsoLogin.section';

import { LoadingContainer, FormWrapper } from '@components/LoginLayout/Login.styles';
import { ResellerConfig } from '@config/resellers.config';
import { BACKEND_ERRORS_TRANSLATION_KEYS } from '@constants/errors.constants';
import { Loading } from '@dashboard/library';
import { useLoginRedirect } from '@hooks/useLoginRedirect/useLoginRedirect';
import { useQueryParams } from '@hooks/useQueryParams';
import { useFeatureIsOn } from '@services/growthbook/attributes';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FeaturesReady } from '@growthbook/growthbook-react';
import UnifiedLoginPage from '../unified-login/UnifiedLogin.page';
import { FEATURE_FLAGS } from '@constants/feature-flag.constants';

interface LocationState {
  from?: string;
  search?: string;
}

export default function LoginPage() {
  const {
    actions: { signInSsoWithCode, setSignInError },
    authState: { signingIn, mfaInitiated },
  } = useAuthenticationState();
  const { searchParams } = useQueryParams();
  const location = useLocation();
  const code = searchParams.get('code');
  const hasSamlError = searchParams.has('error');

  // Get original intended destination from navigation state or current location as fallback
  const locationState = location.state as LocationState | null;
  const originalPathname = locationState?.from ?? location.pathname;
  const originalSearch = locationState?.search ?? location.search;
  const {
    featureSet: { shouldShowSignupLink, useEmailMfa },
  } = ResellerConfig;

  useLoginRedirect();

  useEffect(() => {
    if (code) {
      signInSsoWithCode(code);
    }
    // We only want to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInSsoWithCode]);

  useEffect(() => {
    if (hasSamlError) {
      setSignInError(BACKEND_ERRORS_TRANSLATION_KEYS.CREDENTIALS_ERROR);
    }
  }, [hasSamlError, setSignInError]);

  const isUnifiedLoginEnabled = useFeatureIsOn(FEATURE_FLAGS.USE_UNIFIED_LOGIN_UI);

  const renderLoginComponent = () => {
    if (isUnifiedLoginEnabled) {
      return <UnifiedLoginPage path={originalPathname} search={originalSearch} />;
    }

    if (useEmailMfa && mfaInitiated) {
      return <MFAForm />;
    }

    return (
      <LoginLayout data-test='login-page'>
        <FormWrapper>
          {/* We want this loading to be on top to avoid re-rendering the login form and losing its state */}
          {signingIn && (
            <LoadingContainer>
              <Loading data-test='login-page-loading' />
            </LoadingContainer>
          )}

          <Routes>
            <Route path={LOGIN_SUB_PASSWORD_ROUTE} element={<PasswordLoginSection />} />
            <Route path={LOGIN_SUB_SSO_ROUTE} element={<SsoLoginSection />} />
          </Routes>

          {shouldShowSignupLink && <SignupButton />}
        </FormWrapper>
      </LoginLayout>
    );
  };

  return (
    <FeaturesReady
      timeout={1000}
      fallback={
        <LoginLayout data-test='login-page-loading'>
          <FormWrapper>
            <LoadingContainer>
              <Loading data-test='login-page-loading' />
            </LoadingContainer>
          </FormWrapper>
        </LoginLayout>
      }
    >
      {renderLoginComponent()}
    </FeaturesReady>
  );
}
